import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from "react-router";
import Modal from "../../components/Modal";
import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';
import { Location } from 'history';
import client from 'lib/CommonApi/client';
import { callAlertWeb, callNativeWeb, footerShowEventWeb, uploadPhotoIdWeb } from 'common/appInterface';
import { useLoading } from 'components/Loading/LoadingProvider';

const IdcardUploadPage: React.FC = () => {
  /** Location State */
  const location = useLocation() as Location<LocationState>;
  /** Navigate */
  const navigate = useNavigate();
  /** 다국어처리 */
  const { t, i18n } = useTranslation();

  const { showLoading, hideLoading } = useLoading();

  const screenId = "APPL09";
  const screenName = t("APPL09.title");

  const [cardApply] = useState<Partial<CardApplyStep4>>(location.state.apply);

  // const targetList = (cardApply.idcardDivisionCode === "4") ? ["41", "43"] : Array.from({ length: 3 }, (_, i) => cardApply.idcardDivisionCode! + (i + 1));
  /** 2023.05.26 셀픽 제거  */
  const targetList = (cardApply.idcardDivisionCode === "4") ? ["41"] : Array.from({ length: 2 }, (_, i) => cardApply.idcardDivisionCode! + (i + 1));
  const [completList, setCompletList] = useState<string[]>([]);
  const [isModal, setModal] = useState(false);
  const [isClick, setClick] = useState(false);

  /** 뒤로가기 처리 (backKeyEvent_App (APP > WEB)) */
  const callbackBackkeyEvent = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  /** 모달 닫기 */
  const closeModal = useCallback(() => {
    setModal(false);
    navigate("/main", { state: { query: location.state.query, swiperIndex: location.state.swiperIndex, tabIndex: location.state.tabIndex }});
  }, [navigate, location]);

  // TODO 테스트용
  const testCall = (type: string) => {
    let num = type.substr(-1);
    let url = `${process.env.REACT_APP_LOCALHOST}/assets/images/content/gmoney-card_` + (num === "1" ? "100921" : num === "2" ? "100922" : "101007") + ".png";
    client.get(url, { responseType: 'arraybuffer' }).then((response) => {
      let imageValue = Buffer.from(response.data, "base64").toString("base64");
      let idType = type.substring(0, 1);
      let photoType = type.substring(1);
      let event = new CustomEvent("idUpload", { detail: { idType: idType, photoType: photoType, imageValue: encodeURIComponent(imageValue) }});
      document.dispatchEvent(event);
    });
  };

  /** 신분증촬영 요청 (uploadPhotoID_Web (WEB > APP)) */
  const clickUploadPhotoId = useCallback((type: string) => {
    let idType = type.substring(0, 1);
    let photoType = type.substring(1);

    uploadPhotoIdWeb(idType, photoType);
  }, []);

  /** 신분증촬영 결과값 전달 (completedPhotoID_App (APP > WEB)) */
  const callbackCompletedPhotoIdApp = useCallback((event) => {
    setCompletList({ ...completList, [event.detail.idType + event.detail.photoType]: decodeURIComponent(event.detail.imageValue) });
  }, [completList]);

  /** 신분증 업로드 */
  const callIdcardUpload = useCallback(async () => {
    if (isClick) {
      console.log('Already Clicked...');
      return;
    }

    setClick(true);
    showLoading();
    await client
      .post('/api/app/apply/idcard/upload', {
        ...location.state.query,
        receiveSeq: cardApply.receiveSeq,
        receiveDate: cardApply.receiveDate,
        idcardDivisionCode: cardApply.idcardDivisionCode,
        idcardList: completList
      })
      .then((response) => {
        if (response.data.code === '00') {
          navigate('/apply/step5', {
            state: { ...location.state, apply: cardApply }
          });
        } else {
          callAlertWeb('ERROR', response.data.code, response.data.message);
        }
      })
      .catch((e: AxiosError) => {
        callAlertWeb('ERROR', '99', e.message);
      })
      .finally(() => {
        hideLoading();
        setClick(false);
      });
  }, [
    isClick,
    location.state,
    cardApply,
    completList,
    navigate,
    showLoading,
    hideLoading
  ]);

  /** 신분증 재업로드 */
  const callIdcardReupload = useCallback(async () => {
    showLoading();
    await client
      .post('/api/app/apply/idcard/reupload', {
        ...location.state.query,
        receiveSeq: cardApply.receiveSeq,
        receiveDate: cardApply.receiveDate,
        idcardDivisionCode: cardApply.idcardDivisionCode,
        idcardList: completList
      })
      .then((response) => {
        if (response.data.code === '00') {
          setModal(true);
        } else {
          callAlertWeb('ERROR', response.data.code, response.data.message);
        }
      })
      .catch((e: AxiosError) => {
        callAlertWeb('ERROR', '99', e.message);
      })
      .finally(() => {
        hideLoading();
      });
  }, [location, cardApply, completList, showLoading, hideLoading]);

  // 최초실행
  useEffect(() => {
    // 언어설정
    i18n.changeLanguage(location.state.query.languageCode);
    // callNative_Web 처리 (WEB > APP)
    callNativeWeb(screenId, screenName);
    // 하단바 숨기기
    footerShowEventWeb("N");

    console.log("QUERY STATE");
    console.log(location.state);
    console.log(cardApply);
    console.log(targetList);
    console.log(completList);

    // 최초한번실행 useEffect() Warning 제거
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 뒤로가기 이벤트 리스너 (backKeyEvent_App (APP > WEB))
  useEffect(() => {
    document.addEventListener("backpress", callbackBackkeyEvent);
    return () => document.removeEventListener("backpress", callbackBackkeyEvent);
  }, [callbackBackkeyEvent]);
  
  // 신분증촬영 결과전달 이벤트 리스너 (completedPhotoID_App (APP > WEB))
  useEffect(() => {
    document.addEventListener("idUpload", callbackCompletedPhotoIdApp);
    return () => document.removeEventListener("idUpload", callbackCompletedPhotoIdApp);
  }, [callbackCompletedPhotoIdApp]);

  return (
    <div id="wrap">
      <div id="container" className="pcHidden">
        <section className="contents photoup">
          <div className="inner">
            <div className="settingListWrap clfix">
              <dl className="settingList">
                <dt><span className="tit">{t(`APPL09.explan01.${cardApply.idcardDivisionCode}`)}</span></dt>
                {targetList.map((value) => 
                  <dd key={value}>
                    <button type="button" onClick={() => clickUploadPhotoId(value)}>
                      <div className="camera">
                        <img src={completList.hasOwnProperty(value) ? "/assets/images/ico/icon_checked.png" : "/assets/images/ico/icon_camera.png"} alt="" />
                      </div>
                      <span className="tit">{t(`APPL09.select.${value}`)}</span>
                    </button>
                  </dd>
                )}
              </dl>
            </div>
          </div>
          {process.env.REACT_APP_PROFILE !== "prod" && targetList.map((value) =>
            <button key={value} className="btnPrimaryTy01" type="button" onClick={() => testCall(value)}><span>{value} 업로드</span></button>
          )}
        </section>
        <div className="btnArea">
          <div className="btnWrapTy01">
            {!cardApply.isReupload &&
              <button type="button" className="btnPrimaryTy01" disabled={Object.keys(completList).length !== targetList.length || isClick} onClick={callIdcardUpload}><span>{t("COMMON.button.next")}</span></button>
            }
            {cardApply.isReupload &&
              <button type="button" className="btnPrimaryTy01" disabled={Object.keys(completList).length !== targetList.length || isClick} onClick={callIdcardReupload}><span>{t("COMMON.button.done")}</span></button>
            }
          </div>
        </div>
      </div>
      <Modal isOpen={isModal} contentLabel="modal" onRequestClose={closeModal} className="popTyLayer2" overlayClassName="dim" closeTimeoutMS={500}>
        {/*<p className="tit">{t("STAT03.popup01")}</p>*/}
        <p className="message">{t("STAT03.popup04")}</p>
        <div className="btnWrapTy02">
          <button type="button" className="btnPrimaryTy01" onClick={closeModal}><span>{t("COMMON.button.confirm")}</span></button>
        </div>
      </Modal>
    </div>
  );
};

export default IdcardUploadPage;